// Other people's farm work, played on your screen (owner 2026-10-03): the host's Kombajn and hand jobs on a visitor's
// screen, a visitor's watering / feeding / petting on the host's — what they see, at their spot, their sounds at about
// half volume. One vehicle and one progress bar per peer. The fields themselves still come from the server
// (farm_changed → reload); this is only the work being done. Messages: `farm_fx` (farmFx.mjs).
import { realtime } from '../api/realtime.js';
import { audioEvents } from '../audio/audioEvents.js';
import { animalSoundEvent } from '../audio/eventMap.js';
import { iconText } from '../ui/iconText.js';
import { dir8FromVector } from '../ui/walkAnim.js';
import { FarmVehicle } from './FarmVehicle.js';
import { waterDrop } from './farmFxArt.js';
import { fxPlan, PEER_GAIN } from './farmFx.mjs';
import { reconcileRoster } from '../ui/rosterReconcile.mjs';

const TILE_H = 32;

export function attachFarmFx(scene, room, { getAnimals = () => [] } = {}) {
  const peers = new Map();                             // peerId -> { vehicle, bar }

  const peerOf = (id) => {
    if (!peers.has(id)) peers.set(id, { vehicle: null, bar: null });
    return peers.get(id);
  };
  const avatarOf = (id) => scene._presence?.avatars?.get(id)?.sprite ?? null;

  const makeBar = () => {
    const container = scene.add.container(0, 0);
    const bg = scene.add.rectangle(0, 0, 64, 12, 0x000000, 0.7).setStrokeStyle(1, 0xffffff, 0.6);
    const fill = scene.add.rectangle(-30, 0, 0, 8, 0x7ed957, 1).setOrigin(0, 0.5);
    const icon = iconText(scene, 0, -22, '', { fontSize: '22px' }).setOrigin(0.5);
    container.add([bg, fill, icon]).setDepth(1e9);
    scene.world.objects.add(container);
    return { container, fill, icon };
  };

  const floatAt = (at, label) => {
    const t = iconText(scene, at.x, at.y - TILE_H, label, { fontFamily: 'system-ui', fontSize: '18px', color: '#ffe066',
      fontStyle: 'bold', stroke: '#000', strokeThickness: 3 }).setOrigin(0.5).setDepth(1e9);
    scene.world.objects.add(t);
    scene.tweens.add({ targets: t, y: t.y - 40, alpha: 0, duration: 900, ease: 'Cubic.Out', onComplete: () => t.destroy() });
  };

  const sound = (s) => {
    if (!s) return;
    if (typeof s === 'string') { audioEvents.emit(s, { gain: PEER_GAIN }); return; }
    const species = getAnimals().find((a) => a.id === s.id)?.type;
    const ev = animalSoundEvent(s.animal, species);
    if (ev) audioEvents.emit(ev, { gain: PEER_GAIN });
  };

  const play = (m) => {
    if (m.room !== room || !scene.world) return;
    const plan = fxPlan(m);
    if (!plan) return;
    const p = peerOf(m.peerId);
    if (plan.vehicle?.start) {
      p.vehicle ??= new FarmVehicle(scene, scene.world);
      const v = plan.vehicle.start;
      p.vehicle.popIn(v.tool, v.size, v.at, v.action);
    }
    if (plan.vehicle?.progress != null) p.vehicle?.setProgress(plan.vehicle.progress);
    if (plan.vehicle?.end) p.vehicle?.popOut();
    if (plan.bar?.start) {
      p.bar ??= makeBar();
      p.bar.icon.setText(plan.bar.start); p.bar.fill.width = 0; p.bar.container.setVisible(true);
      const a = avatarOf(m.peerId);
      if (a && plan.bar.face) a.setFacing8?.(dir8FromVector(plan.bar.face.x - a.x, plan.bar.face.y - (a.feetY ?? a.y)));
    }
    if (plan.bar?.progress != null && p.bar) p.bar.fill.width = 60 * plan.bar.progress;
    if (plan.bar?.end && p.bar) p.bar.container.setVisible(false);
    if (plan.float) floatAt(plan.float.at, plan.float.label);
    if (plan.hearts) scene.world.animalLayer?.hearts(plan.hearts);
    if (plan.drop) waterDrop(scene, plan.drop.x, plan.drop.y);
    sound(plan.sound);
  };

  // the bar rides above its worker
  const follow = () => {
    for (const [id, p] of peers) {
      const a = avatarOf(id);
      if (p.bar?.container.visible && a) p.bar.container.setPosition(a.x, a.y - 70);
    }
  };
  const off = realtime.on('farm_fx', play);
  scene.events.on('update', follow);
  const offLeave = realtime.on('leave', (m) => {
    if (m.room !== room) return;
    const p = peers.get(m.peerId);
    p?.vehicle?.destroy(); p?.bar?.container.destroy(); peers.delete(m.peerId);
  });
  // Back from being inactive (or a dropped socket): work bars of farmers who left meanwhile go too.
  const offRoster = realtime.on('roster', (m) => {
    if (m.room !== room) return;
    for (const id of reconcileRoster(peers.keys(), m.peers).stale) {
      const p = peers.get(id);
      p?.vehicle?.destroy(); p?.bar?.container.destroy(); peers.delete(id);
    }
  });
  const detach = () => {
    off(); offLeave(); offRoster(); scene.events.off('update', follow);
    for (const p of peers.values()) { p.vehicle?.destroy(); p.bar?.container.destroy(); }
    peers.clear();
  };
  scene.events.once('shutdown', detach);
  return { detach };
}
